import React from 'react';
import { Helmet } from 'react-helmet-async';
import { LegalEntityDetails } from '../components/common/LegalEntityDetails';
import { registrationIdentifiers } from '../lib/jsonLd';

export function TermsPage() {
  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Fundacja Znajdki',
    legalName: 'Fundacja „Znajdki"',
    url: 'https://znajdki.pl',
    taxID: '1132860384',
    vatID: 'PL1132860384',
    identifier: registrationIdentifiers
  } as const;

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Regulamin | Fundacja Znajdki',
    url: 'https://znajdki.pl/regulamin',
    description:
      'Regulamin korzystania ze strony Fundacji Znajdki oraz warunki świadczenia usług drogą elektroniczną.',
    isPartOf: {
      '@type': 'Organization',
      name: 'Fundacja Znajdki',
      url: 'https://znajdki.pl'
    }
  } as const;

  return (
    <div className="max-w-4xl mx-auto px-4 py-16">
      <Helmet>
        <title>Regulamin | Fundacja "Znajdki"</title>
        <meta name="description" content="Regulamin korzystania ze strony Fundacji Znajdki oraz warunki świadczenia usług drogą elektroniczną." />
        <meta property="og:title" content="Regulamin | Fundacja Znajdki" />
        <meta property="og:description" content="Zasady korzystania z serwisu i świadczenia usług drogą elektroniczną." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>
      </Helmet>

      <header className="mb-10">
        <h1 className="heading-1">Regulamin</h1>
        <p className="body-medium mt-4 text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
        Obowiązuje od: 30.12.2025        </p>
      </header>

      <section className="space-y-8 text-accessible-gray-700 dark:text-accessible-gray-200 high-contrast:text-white">
        <LegalEntityDetails title="Dane rejestrowe Usługodawcy" />

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Postanowienia ogólne</h2>
          <p>
            Niniejszy Regulamin określa zasady korzystania z serwisu internetowego prowadzonego przez Fundację Znajdki,
            a także warunki świadczenia usług drogą elektroniczną, w tym zasady kontaktu oraz odpowiedzialności.
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Definicje</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Serwis – strona internetowa dostępna pod adresem znajdki.pl.</li>
            <li>Użytkownik – każda osoba korzystająca z Serwisu.</li>
            <li>Usługi – funkcjonalności Serwisu dostępne bezpłatnie dla Użytkowników.</li>
          </ul>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Zasady korzystania</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Użytkownik zobowiązany jest do korzystania z Serwisu zgodnie z prawem i dobrymi obyczajami.</li>
            <li>Zakazane jest dostarczanie treści bezprawnych.</li>
            <li>Fundacja dokłada starań, aby Serwis działał nieprzerwanie i bez błędów.</li>
          </ul>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Usługi świadczone drogą elektroniczną</h2>
          <p>
            Korzystanie z Serwisu nie wymaga rejestracji. W ramach Serwisu udostępniamy informacje, formularze kontaktowe
            oraz treści edukacyjne i informacyjne związane z działalnością Fundacji.
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Newsletter</h2>
          <p className="mb-4">
            Serwis oferuje możliwość zapisania się do newslettera, w ramach którego Użytkownicy otrzymują informacje o działalności Fundacji,
            aktualnościach oraz zwierzętach poszukujących domu.
          </p>
          <h3 className="heading-5 mb-2 mt-4">Dostawca usługi</h3>
          <p className="mb-4">
            Do obsługi newslettera wykorzystujemy platformę Mailchimp (The Rocket Science Group LLC Mailchimp),
            która umożliwia zarządzanie bazą subskrybentów oraz wysyłkę wiadomości e-mail. Mailchimp działa jako podmiot przetwarzający dane
            w naszym imieniu i przetwarza dane wyłącznie zgodnie z naszymi instrukcjami.
          </p>
          <h3 className="heading-5 mb-2 mt-4">Zakres przetwarzanych danych</h3>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>Adres e-mail podany przez Użytkownika w formularzu zapisu.</li>
            <li>Data i godzina zapisu do newslettera.</li>
            <li>Informacje techniczne dotyczące interakcji z wysyłanymi wiadomościami (np. otwarcia, kliknięcia w linki).</li>
          </ul>
          <h3 className="heading-5 mb-2 mt-4">Cel przetwarzania</h3>
          <p className="mb-4">
            Dane są przetwarzane wyłącznie w celu wysyłania newslettera po wyrażeniu przez Użytkownika zgody.
            Podstawą prawną przetwarzania jest zgoda Użytkownika (art. 6 ust. 1 lit. a RODO).
          </p>
          <h3 className="heading-5 mb-2 mt-4">Prawa Użytkownika</h3>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>Użytkownik może w każdej chwili zrezygnować z subskrypcji newslettera poprzez kliknięcie w link rezygnacji 
                znajdujący się w każdej wysłanej wiadomości.</li>
            <li>Użytkownik ma prawo dostępu do swoich danych, ich sprostowania, usunięcia, ograniczenia przetwarzania, 
                przenoszenia danych oraz prawo do cofnięcia zgody w dowolnym momencie.</li>
            <li>W celu realizacji swoich praw Użytkownik może skontaktować się z Fundacją pod adresem: 
                <a className="underline ml-1" href="mailto:znajdki2012@gmail.com">znajdki2012@gmail.com</a>.</li>
          </ul>
          <h3 className="heading-5 mb-2 mt-4">Bezpieczeństwo danych</h3>
          <p className="mb-4">
            Mailchimp zapewnia odpowiednie środki techniczne i organizacyjne do ochrony danych osobowych.
            Więcej informacji o polityce prywatności Mailchimp można znaleźć na stronie: 
            <a className="underline ml-1 break-all" href="https://www.intuit.com/privacy/statement/" target="_blank" rel="noopener noreferrer">
              https://www.intuit.com/privacy/statement/
            </a>.
          </p>
          <h3 className="heading-5 mb-2 mt-4">Transfer danych</h3>
          <p>
            Mailchimp jest spółką amerykańską, co oznacza, że dane mogą być przetwarzane poza Europejskim Obszarem Gospodarczym (EOG).
            Transfer danych odbywa się zgodnie z obowiązującymi przepisami o ochronie danych osobowych, w tym na podstawie odpowiednich
            zabezpieczeń prawnych zapewniających odpowiedni poziom ochrony.
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Wymagania techniczne</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Aktualna przeglądarka internetowa z włączoną obsługą JavaScript i plików cookies.</li>
            <li>Dostęp do sieci Internet.</li>
          </ul>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Prawa autorskie</h2>
          <p>
            Treści publikowane w Serwisie są chronione prawem autorskim. Kopiowanie i rozpowszechnianie materiałów
            wymaga zgody Fundacji lub uprawnionych podmiotów, chyba że wyraźnie wskazano inaczej.
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Odpowiedzialność</h2>
          <p>
            Fundacja nie ponosi odpowiedzialności za przerwy w działaniu Serwisu spowodowane siłą wyższą lub działaniami osób trzecich,
            z zastrzeżeniem bezwzględnie obowiązujących przepisów prawa.
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Dane kontaktowe</h2>
          <p>
            Kontakt: <a className="underline" href="mailto:znajdki2012@gmail.com">znajdki2012@gmail.com</a>.
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Postanowienia końcowe</h2>
          <p>
            Regulamin może ulegać zmianom. O istotnych zmianach poinformujemy poprzez publikację zaktualizowanej treści na tej stronie wraz z datą obowiązywania.
          </p>
        </div>
      </section>
    </div>
  );
}



